import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import Button from '../../../components/ui/Button';
import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, Sub, Table, TabButton, Tabs } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { Hidden, Note } from '../../../components/performance/evaluation/EvaluationParts';
import {
  formatNum, formatSigned, isSuppressed, metaOfContext, tint,
} from '../../../components/performance/evaluation/evaluationAnalytics';
import {
  HEATMAP_COLUMNS_PER_PAGE, NS, dimensionsFor, fetchHeatmap,
} from '../config';

// E05 orta alan — yetkinlik satırları × organizasyon sütunları ısı haritası; her hücrede fark (mevcut − beklenen), n ve beklenen
// seviye. Gizlilik sunucuda: n < k hücre ve tamamlayıcı hücre değer TAŞIMAZ (burada yalnız "Gizli" çizilir, tıklanamaz).
// Renk değeri metinle desteklenir (fark + n her hücrede yazılı); "Tablo Olarak Göster" aynı veriyi klavyeyle gezilebilir satırlarla
// verir. Hücre (ya da tablo satırı) → aynı yetkinlik ve birimde kayıt listesi.
const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1.6fr) repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
  gap: 4px;
  min-width: 0;
`;

const HeadCell = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.muted};
  padding: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  align-self: end;
`;

const RowHead = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 6px 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  align-self: center;
`;

const CellBox = styled.div`
  min-height: 44px;
  border-radius: 6px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  min-width: 0;
`;

const CellValue = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CellMeta = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Swatch = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  margin-right: 4px;
  vertical-align: middle;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

function cellBackground(theme, gap) {
  if (gap === null || gap === undefined) return theme.colors.surface.card;
  const g = Number(gap);
  const alpha = Math.min(1, Math.abs(g) / 2) * 0.55 + 0.08;
  if (g < 0) return tint(theme.colors.auxiliar.danger, alpha);
  if (g > 0) return tint(theme.colors.secondary.main, alpha);
  return theme.colors.bgColor;
}

function HeatCell({ cell, onOpen, label }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lang = i18n.language;
  if (isSuppressed(cell)) return <CellBox $bg={theme.colors.bgColor} data-cell={cell.key}><Hidden ns={NS} /></CellBox>;
  if (!cell || !cell.n) return <CellBox $bg={theme.colors.surface.card} data-cell={cell?.key} data-empty="1"><CellMeta>-</CellMeta></CellBox>;
  const body = (
    <CellBox $bg={cellBackground(theme, cell.gap)} data-cell={cell.key}>
      <CellValue>{formatSigned(cell.gap, lang)}</CellValue>
      <CellMeta>{t(`${NS}.heatmap.cellMeta`, { n: formatNum(cell.n, lang, 0), expected: formatNum(cell.expected, lang) })}</CellMeta>
    </CellBox>
  );
  return onOpen ? (
    <Clickable onClick={onOpen} aria-label={label} data-open-cell={cell.key} style={{ display: 'block' }}>{body}</Clickable>
  ) : body;
}

export default function CompetencyHeatmap({
  filters, screen, dimension, onDimension, onOpen, onMeta, isHr = false,
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const [colPage, setColPage] = useState(0);
  const key = JSON.stringify([filters, screen, dimension]);
  const panel = usePanel(() => fetchHeatmap(filters, screen, dimension), [key], !!filters.periodId);
  useEffect(() => { if (panel.data) onMeta?.('heatmap', metaOfContext(panel.data)); }, [panel.data, onMeta]);
  useEffect(() => { setColPage(0); }, [key]);

  const columns = panel.data?.columns || [];
  const rows = panel.data?.rows || [];
  const pages = Math.max(1, Math.ceil(columns.length / HEATMAP_COLUMNS_PER_PAGE));
  const page = Math.min(colPage, pages - 1);
  const visible = columns.slice(page * HEATMAP_COLUMNS_PER_PAGE, (page + 1) * HEATMAP_COLUMNS_PER_PAGE);
  const unitLabel = (c) => (c?.key ? (c.label || c.key) : t(`${NS}.heatmap.unassigned`));
  const cellOf = (row, colKey) => row.cells.find((c) => c.key === colKey) || { key: colKey, n: 0 };
  // Atanmamış (null) sütun ortak süzgeçle ifade edilemez → yalnız yetkinlik süzgeciyle açılır.
  const open = (row, column) => onOpen?.({ competencyId: row.competencyId, dimension, unitKey: column?.key || null });

  const actions = (
    <Tabs role="tablist" style={{ marginBottom: 0 }}>
      {dimensionsFor(isHr).map((d) => (
        <TabButton key={d} role="tab" aria-selected={dimension === d} $active={dimension === d} onClick={() => onDimension(d)} data-dimension={d}>
          {t(`${NS}.dimension.${d}`)}
        </TabButton>
      ))}
    </Tabs>
  );

  return (
    <PanelCard
      panelKey="heatmap" title={t(`${NS}.panel.heatmap`)} span={12}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      emptyText={t(`${NS}.heatmap.empty`)}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)} actions={actions}
    >
      <Legend>
        <span><Swatch $bg={cellBackground(theme, -1.5)} />{t(`${NS}.heatmap.legendBelow`)}</span>
        <span><Swatch $bg={cellBackground(theme, 1.5)} />{t(`${NS}.heatmap.legendAbove`)}</span>
        <span>{t(`${NS}.heatmap.legendNote`, { k: panel.data?.context?.threshold ?? '-' })}</span>
      </Legend>
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t(`${NS}.col.competency`)}</th>
              <th>{t(`${NS}.dimension.${dimension}`)}</th>
              <th>{t(`${NS}.col.gap`)}</th>
              <th>{t(`${NS}.col.expected`)}</th>
              <th>{t(`${NS}.col.current`)}</th>
              <th>{t(`${NS}.col.n`)}</th>
            </tr>
          </thead>
          <tbody>
            {rows.flatMap((row) => [{ column: null, cell: row.total, total: true }, ...columns.map((c) => ({ column: c, cell: cellOf(row, c.key) }))]
              .filter((x) => x.total || x.cell.n || isSuppressed(x.cell))
              .map((x) => {
                const hidden = isSuppressed(x.cell);
                const label = x.total ? t(`${NS}.heatmap.total`) : unitLabel(x.column);
                return (
                  <tr key={`${row.competencyId}:${x.total ? 'total' : x.column.key || 'none'}`} data-row={`${row.competencyId}:${x.total ? 'total' : x.column.key || 'none'}`}>
                    <td title={row.name}>
                      {hidden ? row.name : (
                        <Clickable onClick={() => open(row, x.column)} data-open-row="1">{row.name}</Clickable>
                      )}
                    </td>
                    <td title={label}>{label}</td>
                    <td>{hidden ? <Hidden ns={NS} /> : formatSigned(x.cell.gap, lang)}</td>
                    <td>{hidden ? '' : formatNum(x.cell.expected, lang)}</td>
                    <td>{hidden ? '' : formatNum(x.cell.current, lang)}</td>
                    <td>{hidden ? '' : formatNum(x.cell.n, lang, 0)}</td>
                  </tr>
                );
              }))}
          </tbody>
        </Table>
      ) : (
        <>
          <Grid $cols={visible.length + 1} role="grid" aria-label={t(`${NS}.panel.heatmap`)}>
            <HeadCell>{t(`${NS}.col.competency`)}</HeadCell>
            <HeadCell title={t(`${NS}.heatmap.total`)}>{t(`${NS}.heatmap.total`)}</HeadCell>
            {visible.map((c) => <HeadCell key={c.key || 'none'} title={unitLabel(c)}>{unitLabel(c)}</HeadCell>)}
            {rows.map((row) => (
              <React.Fragment key={row.competencyId}>
                <RowHead title={row.groupName ? `${row.name} · ${row.groupName}` : row.name}>
                  {row.name}
                  {row.groupName ? <Sub>{row.groupName}</Sub> : null}
                </RowHead>
                <HeatCell cell={row.total} label={`${row.name} · ${t(`${NS}.heatmap.total`)}`} onOpen={() => open(row, null)} />
                {visible.map((c) => (
                  <HeatCell
                    key={c.key || 'none'}
                    cell={cellOf(row, c.key)}
                    label={`${row.name} · ${unitLabel(c)}`}
                    onOpen={() => open(row, c)}
                  />
                ))}
              </React.Fragment>
            ))}
          </Grid>
          {pages > 1 ? (
            <Pager>
              <span>{t(`${NS}.heatmap.columns`, { from: page * HEATMAP_COLUMNS_PER_PAGE + 1, to: page * HEATMAP_COLUMNS_PER_PAGE + visible.length, total: columns.length })}</span>
              <Button variant="ghost" onClick={() => setColPage(page - 1)} disabled={page === 0} aria-label={t(`${NS}.heatmap.prev`)} data-action="cols-prev">
                <ChevronLeft size={16} />
              </Button>
              <Button variant="ghost" onClick={() => setColPage(page + 1)} disabled={page >= pages - 1} aria-label={t(`${NS}.heatmap.next`)} data-action="cols-next">
                <ChevronRight size={16} />
              </Button>
            </Pager>
          ) : null}
          <Note>{t(`${NS}.heatmap.hint`)}</Note>
        </>
      )}
    </PanelCard>
  );
}
